import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import EditOutlined from '@ant-design/icons/lib/icons/EditOutlined';
import MinusCircleOutlined from '@ant-design/icons/lib/icons/MinusCircleOutlined';
import PlusOutlined from '@ant-design/icons/lib/icons/PlusOutlined';

import { OpList } from 'new-components/DLS/OpList/OpList';
import { OpForm } from 'new-components/DLS/OpForm/OpForm';
import { useSelectorJs } from 'utils/customHooks';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { redFlagReadScopes } from 'constants/visitorScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive, heliumSend } from 'utils/helpers';
import { Loader } from 'components/Loader';

import { formatFullName } from '../../../../../utils/utils';
import {
  getStatusColor,
  isVisitorRedFlagged,
  profileIcon,
} from '../../../../../utils/visitorsHelper';
import STATUS from '../../../../../constants/status';
import warningIcon from '../../../../../images/icons/warning.svg';
import RedFlagMatchModal from '../../../../../pages/visitors/drawer/profile/RedFlagMatchModal';
import {
  fetchRedFlags,
  RedFlagState,
  setSelectedRedFlaggedVisitor,
} from '../../../../../store/slices/redFlagSlice';
import {
  fetchVisitorTypes,
  VisitorTypesState,
} from '../../../../../store/slices/visitorTypesSlice';
import { AppDispatch, ImmutableRootState } from '../../../../../store/store';
import { VisitorType } from '../../../../../types/VisitorTypeTypes';
import { VisitorWorkflowFields } from '../../../../../types/visitorWorkflowTypes';
import { Visitor } from '../../../../../types/visitTypes';

import VisitorListModal, {
  VisitorListModalSubmitData,
} from './VisitorListModal';

interface VisitorListProps {
  signInWorkflowFields?: VisitorWorkflowFields;
  isInviting: boolean;
  isLoading?: boolean;
  newVisitorName?: {
    firstName: string;
    middleName: string;
    lastName: string;
    dob?: string;
  };
  locationVisitorTypes?: VisitorType[];
  selectedVisitorTypeId?: number;
  setNewVisitorName?: (
    filter:
      | {
          firstName: string;
          middleName: string;
          lastName: string;
          dob?: string;
        }
      | undefined,
  ) => void;
  onVisitorTypeChange?: (value: number) => void;
}

type VisitorWithError = Visitor & { error?: string };

interface CheckrIntegrationResponse {
  data?: unknown[] | null;
}

interface CheckrRecordResponse {
  data?: {
    results?: ExternalRedFlagRecord[] | null;
  } | null;
}

interface ExternalRedFlagRecord {
  id?: string | number;
  person?: {
    full_name?: string;
    dob?: string;
    first_name?: string;
    last_name?: string;
  };
  category?: string;
}

const VisitorList = ({
  signInWorkflowFields,
  isInviting,
  isLoading,
  newVisitorName,
  locationVisitorTypes,
  selectedVisitorTypeId,
  setNewVisitorName,
  onVisitorTypeChange,
}: VisitorListProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { isValid: hasRedFlagRead } = useValidateScopes(redFlagReadScopes);

  const redFlags = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['redFlags', 'redFlags']),
  ) as RedFlagState['redFlags'];
  const visitorTypes = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorTypes', 'visitorTypes']),
  ) as VisitorTypesState['visitorTypes'];
  const fetchVisitorTypesLoading = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorTypes', 'fetchVisitorTypesLoading']),
  ) as VisitorTypesState['fetchVisitorTypesLoading'];

  const [isVisitorModalOpen, setIsVisitorModalOpen] = useState(true);
  const [isRedFlagMatchModalOpen, setIsRedFlagMatchModalOpen] = useState(false);
  const [currentVisitor, setCurrentVisitor] = useState<Visitor | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingExternalRedFlags, setLoadingExternalRedFlags] =
    useState<boolean>(false);
  const [isCheckrInstalled, setIsCheckrInstalled] = useState(false);
  const [visitorExternalRedFlags, setVisitorExternalRedFlags] = useState<{
    [key: number]: ExternalRedFlagRecord[];
  }>({});
  const [selectedExternalRedFlags, setSelectedExternalRedFlags] = useState<
    ExternalRedFlagRecord[]
  >([]);

  const form = OpForm.useFormInstance();
  const handleOpenModal = (visitor?: Visitor) => {
    setCurrentVisitor(visitor || null);
    setIsVisitorModalOpen(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Guarded on redFlagReadScopes because the helium GET /redflag route
        // rejects roles without red flag read (e.g. dashboard-only) with a 403.
        if (hasRedFlagRead && redFlags.length === 0) {
          dispatch(fetchRedFlags({ orgId }));
        }

        dispatch(fetchVisitorTypes({ orgId }));

        const checkrIntegrationData = (await heliumReceive(
          'getIntegrationCheckrTrust',
          [orgId],
        )) as CheckrIntegrationResponse;
        if (
          Array.isArray(checkrIntegrationData.data) &&
          checkrIntegrationData.data.length === 0
        ) {
          setIsCheckrInstalled(false);
        } else {
          setIsCheckrInstalled(true);
        }
      } catch {
        // Failed to fetch data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatch, orgId, hasRedFlagRead, redFlags.length]);

  const checkExternalRedFlags = async (visitor: Visitor) => {
    setLoadingExternalRedFlags(true);
    try {
      const dob = visitor.dob ? dayjs(visitor.dob).format('YYYYMMDD') : '';
      const response = (await heliumSend('checkRecordCheckrTrust', [orgId], {
        firstName: visitor.firstName,
        lastName: visitor.lastName,
        dateOfBirth: dob,
      })) as CheckrRecordResponse;

      const externalResults = response.data?.results ?? [];

      if (externalResults.length > 0) {
        setVisitorExternalRedFlags((prev) => ({
          ...prev,
          [visitor.id]: externalResults,
        }));
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      setLoadingExternalRedFlags(false);
    }
  };

  const handleCreateEditVisitor = async (
    visitor: VisitorListModalSubmitData,
  ) => {
    const updatedVisitor: Visitor = {
      id: currentVisitor?.id || Date.now(),
      firstName: visitor.firstName,
      middleName: visitor.middleName ?? null,
      lastName: visitor.lastName,
      email: visitor.email ?? null,
      mobilePhone: visitor.mobilePhone ?? null,
      userId: currentVisitor?.userId ?? null,
      status: currentVisitor?.status ?? STATUS.PENDING.id,
      signIn: currentVisitor?.signIn ?? null,
      signOut: currentVisitor?.signOut ?? null,
      dob: visitor.dob ?? null,
    };

    if (isCheckrInstalled && visitor.dob) {
      await checkExternalRedFlags(updatedVisitor);
    }
    const currentVisitors = form.getFieldValue('visitors') || [];

    if (currentVisitor) {
      const updatedVisitors = currentVisitors.map((v: Visitor) =>
        v === currentVisitor ? updatedVisitor : v,
      );
      form.setFieldsValue({ visitors: updatedVisitors });
    } else {
      form.setFieldsValue({ visitors: [...currentVisitors, updatedVisitor] });
    }
  };

  return (
    <>
      {loading || loadingExternalRedFlags || isLoading ? (
        <Loader />
      ) : (
        <>
          <OpForm.List name="visitors">
            {(
              fields: Array<{ key: number; name: number }>,
              { remove }: { remove: (index: number | number[]) => void },
            ) => (
              <OpList
                locale={{ emptyText: ' ' }}
                dataSource={fields}
                renderItem={(item) => {
                  const field = item as { key: number; name: number };
                  const visitor = form.getFieldValue([
                    'visitors',
                    field.name,
                  ]) as VisitorWithError | undefined;
                  const visitorId = visitor?.id;
                  const externalRedFlags =
                    visitorId !== undefined
                      ? visitorExternalRedFlags[visitorId]
                      : undefined;
                  const isRedFlagged = visitor
                    ? isVisitorRedFlagged(visitor, redFlags, externalRedFlags)
                    : false;

                  const avatarIcon = profileIcon({
                    firstName: visitor?.firstName?.trim() || '',
                    lastName: visitor?.lastName?.trim() || '',
                    size: 24,
                    color: isInviting
                      ? getStatusColor('Pending')
                      : visitor?.status === STATUS.DENIED_ENTRY.id
                        ? getStatusColor('Denied Entry')
                        : getStatusColor('Signed In'),
                    marginRight: 0,
                  });

                  const fullName = formatFullName(
                    visitor?.firstName,
                    signInWorkflowFields?.middleName.included === 1
                      ? visitor?.middleName
                      : undefined,
                    visitor?.lastName,
                  );

                  return (
                    <OpList.Item
                      key={field.key}
                      actions={[
                        <EditOutlined
                          key="edit"
                          onClick={() => handleOpenModal(visitor)}
                        />,
                        <MinusCircleOutlined
                          key="remove"
                          onClick={() => remove(field.name)}
                        />,
                      ]}
                    >
                      <OpForm.Item name={[field.name]} noStyle>
                        {' '}
                        <input type="hidden" />{' '}
                      </OpForm.Item>
                      <OpList.Item.Meta
                        avatar={avatarIcon}
                        title={
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <span>{fullName}</span>
                            {isRedFlagged && (
                              <button
                                type="button"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  marginLeft: '8px',
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  if (!visitor) return;
                                  dispatch(
                                    setSelectedRedFlaggedVisitor(visitor),
                                  );
                                  setSelectedExternalRedFlags(
                                    externalRedFlags || [],
                                  );
                                  setIsRedFlagMatchModalOpen(true);
                                }}
                              >
                                <img
                                  src={warningIcon}
                                  alt="Warning"
                                  style={{ width: '16px', height: '16px' }}
                                />
                              </button>
                            )}
                          </div>
                        }
                        description={
                          signInWorkflowFields?.email.included === 1 &&
                          visitor?.email
                        }
                      />
                      {visitor?.error && (
                        <div style={{ color: 'red', marginRight: -35 }}>
                          {visitor.error}
                        </div>
                      )}
                    </OpList.Item>
                  );
                }}
              />
            )}
          </OpForm.List>
          <OpForm.Button
            type="dashed"
            onClick={() => handleOpenModal()}
            block
            icon={<PlusOutlined />}
          >
            {t('visitors.visitorList.add')}
          </OpForm.Button>
        </>
      )}

      {isVisitorModalOpen && (
        <VisitorListModal
          open={isVisitorModalOpen}
          loading={loading || Boolean(isLoading) || fetchVisitorTypesLoading}
          isInviting={isInviting}
          isCheckrInstalled={isCheckrInstalled}
          signInWorkflowFields={signInWorkflowFields}
          visitor={
            currentVisitor ??
            (newVisitorName
              ? {
                  firstName:
                    newVisitorName.firstName !== 'NONE'
                      ? newVisitorName.firstName
                      : undefined,
                  middleName:
                    newVisitorName.middleName !== 'NONE'
                      ? newVisitorName.middleName
                      : undefined,
                  lastName:
                    newVisitorName.lastName !== 'NONE'
                      ? newVisitorName.lastName
                      : undefined,
                  dob:
                    newVisitorName.dob !== 'NONE'
                      ? newVisitorName.dob
                      : undefined,
                }
              : undefined)
          }
          visitors={form.getFieldValue('visitors') || []}
          visitorTypes={visitorTypes.data || []}
          locationVisitorTypes={locationVisitorTypes}
          selectedVisitorTypeId={selectedVisitorTypeId}
          onVisitorTypeChange={onVisitorTypeChange}
          onOk={handleCreateEditVisitor}
          onClose={() => {
            setIsVisitorModalOpen(false);
            if (setNewVisitorName) setNewVisitorName(undefined);
          }}
        />
      )}

      {isRedFlagMatchModalOpen && (
        <RedFlagMatchModal
          visible={isRedFlagMatchModalOpen}
          onClose={() => setIsRedFlagMatchModalOpen(false)}
          externalRedFlags={selectedExternalRedFlags}
        />
      )}
    </>
  );
};

export default VisitorList;
